import React, { useState } from 'react';
import AssessmentComponent from './components/AssessmentComponent';
import { AssessmentResult } from './types/assessment';

function App() {
  const [isAssessmentStarted, setIsAssessmentStarted] = useState(false);

  const handleStartAssessment = () => {
    setIsAssessmentStarted(true);
  };

  const handleAssessmentComplete = (result: AssessmentResult) => {
    console.log('Assessment completed:', result);
  };

  if (isAssessmentStarted) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <AssessmentComponent 
          userId="demo-user-123"
          onComplete={handleAssessmentComplete}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-4xl mx-auto text-center">
        <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            LifeSync Mental Health Assessment
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            A comprehensive, evidence-based mental health assessment tool designed to help you understand your mental wellbeing and receive personalized recommendations.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="font-semibold text-lg mb-2">Evidence-Based</h3>
              <p className="text-gray-600">Uses validated screening tools like PHQ-9 for depression and GAD-7 for anxiety assessment.</p>
            </div>
            <div className="bg-green-50 p-6 rounded-lg">
              <h3 className="font-semibold text-lg mb-2">Comprehensive</h3>
              <p className="text-gray-600">Covers depression, anxiety, stress, trauma, lifestyle factors, and social support.</p>
            </div>
            <div className="bg-purple-50 p-6 rounded-lg">
              <h3 className="font-semibold text-lg mb-2">Personalized</h3>
              <p className="text-gray-600">Generates tailored recommendations and identifies risk factors specific to your responses.</p>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Important:</strong> This assessment is for informational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment. If you're experiencing a mental health emergency, please contact your local emergency services or a crisis hotline immediately.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleStartAssessment}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg text-lg transition duration-200"
            >
              Start Mental Health Assessment
            </button>
            
            <div className="text-sm text-gray-500">
              <p>⏱️ Estimated time: 15-20 minutes</p>
              <p>📝 Approximately 50-70 questions (varies based on your responses)</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4">What You'll Receive</h2>
          <div className="grid md:grid-cols-2 gap-6 text-left">
            <div>
              <h3 className="font-semibold mb-2">📊 Detailed Scores</h3>
              <ul className="text-gray-600 space-y-1">
                <li>• Depression severity (PHQ-9 based)</li>
                <li>• Anxiety levels (GAD-7 based)</li>
                <li>• Stress assessment</li>
                <li>• Lifestyle factors analysis</li>
                <li>• Social support evaluation</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-2">🎯 Personalized Recommendations</h3>
              <ul className="text-gray-600 space-y-1">
                <li>• Self-care strategies</li>
                <li>• Professional resources</li>
                <li>• Lifestyle modifications</li>
                <li>• Crisis support information</li>
                <li>• Evidence-based interventions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App; 